// import moment from 'moment';
import { ensureTrailingSlash } from '../../../utils/url/url';
import Rest from '../../../utils/rest/rest';
import moment from 'moment';
import { calculateDurationSeconds } from '../../../utils/session/session';
import { isInterpreterUser } from '../../../utils/user/user';
import { fetchJoinSessionData } from '../../../utils/session/session';
import { setSessionType, updateRoles, updateStartTime } from '../activeSession/activeSession';

const apiUrl = ensureTrailingSlash(process.env.REACT_APP_API_URL);
const apiPrefix = 'api/v1/session/';

// these are defined in the backend
export const SessionStatus = Object.freeze({
  NEW: 'NEW',
  ONGOING: 'ONGOING',
  FINISHED: 'FINISHED',
});

// session types
export const SessionType = Object.freeze({ TEXT: 'TEXT', VIDEO: 'VIDEO' });

export const Session = Object.freeze({
  SAVE_SESSION_STARTED: 'SAVE_SESSION_STARTED',
  SAVE_SESSION_FINISHED: 'SAVE_SESSION_FINISHED',
  SAVE_SESSION_ERROR: 'SAVE_SESSION_ERROR',
  DELETE_SESSION_STARTED: 'DELETE_SESSION_STARTED',
  DELETE_SESSION_FINISHED: 'DELETE_SESSION_FINISHED',
  DELETE_SESSION_ERROR: 'DELETE_SESSION_ERROR',
  FETCH_SESSIONS_STARTED: 'FETCH_SESSIONS_STARTED',
  FETCH_SESSIONS_FINISHED: 'FETCH_SESSIONS_FINISHED',
  FETCH_SESSIONS_ERROR: 'FETCH_SESSIONS_ERROR',
  CLEAR_SESSIONS: 'CLEAR_SESSIONS',
  UPDATE_SESSION_LIST: 'UPDATE_SESSION_LIST',
  UPDATE_TARGET_SESSION: 'UPDATE_TARGET_SESSION',
  UPDATE_ACTIVE_SESSION_DATA: 'UPDATE_ACTIVE_SESSION_DATA',
  INIT_SESSION_STARTED: 'INIT_SESSION_STARTED',
  INIT_SESSION_FINISHED: 'INIT_SESSION_FINISHED',
  INIT_SESSION_ERROR: 'INIT_SESSION_ERROR',
  FINISH_SESSION_STARTED: 'FINISH_SESSION_STARTED',
  FINISH_SESSION_FINISHED: 'FINISH_SESSION_FINISHED',
  FINISH_SESSION_ERROR: 'FINISH_SESSION_ERROR',
});

const findSessionById = (sessionId, sessions) => {
  return sessions.find((session) => session.sessionId === sessionId);
};

export const initSession = (userData, sessionId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: Session.INIT_SESSION_STARTED });

      let activeSessionData;

      // user is interpreter so expect the session to be in the state already
      const userIsInterpreter = isInterpreterUser(userData);
      const ownSession = findSessionById(sessionId, getState().session.sessions) != null;
      if (userIsInterpreter === true && ownSession === true) {
        // start the session
        const startSessionPath = `${apiUrl}${apiPrefix}start/${sessionId}`;
        activeSessionData = await Rest.get(startSessionPath);
      } else {
        activeSessionData = await fetchJoinSessionData(userData.username, sessionId);
      }

      dispatch({
        type: Session.UPDATE_ACTIVE_SESSION_DATA,
        payload: activeSessionData,
      });

      dispatch(updateRoles(activeSessionData.callData.roles));
      dispatch(setSessionType(activeSessionData.session.type));
      dispatch({ type: Session.INIT_SESSION_FINISHED });

      // activeSessionData.session.actualStartDateTime contains server side start time, but
      // we use client side clock for session duration calculations, so...
      const { duration = 0 } = activeSessionData;
      const clientSideStartTime = +new Date() - 1000 * duration;
      dispatch(updateStartTime(clientSideStartTime));

      return Promise.resolve(activeSessionData);
    } catch (error) {
      console.log('Session init failed with error', error);
      dispatch({ type: Session.INIT_SESSION_ERROR, payload: error });
      return Promise.reject(error);
    }
  };
};

export const finishSession = (session) => {
  return async (dispatch, getState) => {
    try {
      console.log('finishSession - start');

      dispatch({ type: Session.FINISH_SESSION_STARTED });

      const finishSessionPath = `${apiUrl}${apiPrefix}finish/${session.sessionId}`;
      await Rest.get(finishSessionPath);

      // update session list by removing the finished session (goes to past ones)
      const sessions = getState().session.sessions.slice(0);
      const index = sessions.findIndex((sessionItem) => sessionItem._id === session._id);
      sessions.splice(index, 1);

      // update list
      dispatch({ type: Session.UPDATE_SESSION_LIST, payload: sessions });

      // finished
      dispatch({ type: Session.FINISH_SESSION_FINISHED });
      console.log('finishSession - finish');
    } catch (error) {
      console.log('finishSession ERROR', error);
      // error
      dispatch({ type: Session.FINISH_SESSION_ERROR, payload: error });
    }
  };
};

export const createSession = (createData) => {
  return async (dispatch, getState) => {
    try {
      console.log('createSession - start');

      dispatch({ type: Session.SAVE_SESSION_STARTED });

      const { title, dateStr, startTimeStr, estimatedDurationSeconds } = createData;

      // convert date and time to date & time iso string
      const momentDate = moment(dateStr, 'YYYY-MM-DD');
      const momentTime = moment(startTimeStr, ['HH:mm', 'HH.mm']);
      momentDate.set('hour', momentTime.get('hour'));
      momentDate.set('minute', momentTime.get('minute'));

      // create session
      const newSession = {
        title,
        actualDurationSeconds: 0,
        estimatedDurationSeconds,
        dateTime: momentDate.toISOString(),
        status: SessionStatus.NEW,
        type: createData.type,
      };

      const createSessionPath = `${apiUrl}${apiPrefix}`;
      const createdSession = await Rest.post(createSessionPath, newSession);

      // update session list
      let sessions = getState().session.sessions.slice(0);
      sessions = [createdSession].concat(sessions);

      // update list
      dispatch({ type: Session.UPDATE_SESSION_LIST, payload: sessions });

      // finished
      dispatch({ type: Session.SAVE_SESSION_FINISHED });

      console.log('createSession - finish');

      return Promise.resolve(createdSession);
    } catch (error) {
      console.log('createSession ERROR', error);
      // error
      dispatch({ type: Session.SAVE_SESSION_ERROR, payload: error });
    }
  };
};

export const updateSession = (updateData) => {
  return async (dispatch, getState) => {
    try {
      console.log('updateSession - start');

      dispatch({ type: Session.SAVE_SESSION_STARTED });

      const { dateStr, startTimeStr } = updateData;

      // convert date and time to date & time iso string
      const momentDate = moment(dateStr, 'YYYY-MM-DD');
      const momentTime = moment(startTimeStr, ['HH:mm', 'HH.mm']);
      momentDate.set('hour', momentTime.get('hour'));
      momentDate.set('minute', momentTime.get('minute'));

      // update session
      const updateSession = {
        ...updateData,
        dateTime: momentDate.toISOString(),
      };
      const updateSessionPath = `${apiUrl}${apiPrefix}`;
      const updatedSession = await Rest.put(updateSessionPath, updateSession);

      // update session list
      const sessions = getState().session.sessions.slice(0);
      const index = sessions.findIndex((session) => session._id === updateSession._id);
      if (index !== -1) {
        sessions[index] = updatedSession;
      }

      // update list
      dispatch({ type: Session.UPDATE_SESSION_LIST, payload: sessions });

      // finished
      dispatch({ type: Session.SAVE_SESSION_FINISHED });
      console.log('updateSession - finish');

      return Promise.resolve(updatedSession);
    } catch (error) {
      console.log('updateSession ERROR', error);
      // error
      dispatch({ type: Session.SAVE_SESSION_ERROR, payload: error });
    }
  };
};

export const removeSession = (session) => {
  return async (dispatch, getState) => {
    try {
      console.log('removeSession - start');

      dispatch({ type: Session.DELETE_SESSION_STARTED });

      const deleteSessionPath = `${apiUrl}${apiPrefix}${session._id}`;
      await Rest.delete(deleteSessionPath);

      // update session list
      const sessions = getState().session.sessions.slice(0);
      const index = sessions.findIndex((sessionItem) => sessionItem._id === session._id);
      if (index !== -1) {
        sessions.splice(index, 1);
      }

      // update list
      dispatch({ type: Session.UPDATE_SESSION_LIST, payload: sessions });

      // finished
      dispatch({ type: Session.DELETE_SESSION_FINISHED });
      console.log('removeSession - finish');
    } catch (error) {
      console.log('removeSession ERROR', error);
      // error
      dispatch({ type: Session.DELETE_SESSION_ERROR, payload: error });
    }
  };
};

export const fetchSessions = (startIndex, stopIndex, statusesArray = [SessionStatus.NEW]) => {
  return async (dispatch, getState) => {
    try {
      console.log('fetchSessions', startIndex, stopIndex, status, apiUrl);

      // start
      dispatch({ type: Session.FETCH_SESSIONS_STARTED });

      const statuses = statusesArray.join(',');
      const limit = stopIndex - startIndex + 1;
      let path = `${apiUrl}${apiPrefix}?statuses=${statuses}&offset=${startIndex}&limit=${limit}`;

      // set username
      const user = getState().user.userData;
      const { username } = user;
      if (username) {
        path = `${path}&username=${username}`;
      }

      const result = await Rest.get(path);

      let sessions;
      const newSessions = result.docs;
      if (startIndex === 0) {
        sessions = newSessions;
      } else {
        const existingSessions = getState().session.sessions;
        sessions = existingSessions.concat(newSessions);
      }

      console.log('fetchSessions', result);
      // start
      dispatch({
        type: Session.FETCH_SESSIONS_FINISHED,
        payload: {
          sessions,
          overallSessionCount: result.totalDocs,
        },
      });
    } catch (error) {
      console.log('fetchSessions error', error);
      // start
      dispatch({ type: Session.FETCH_SESSIONS_ERROR, payload: error });
    }
  };
};

export const clearSessions = () => {
  return {
    type: Session.CLEAR_SESSIONS,
  };
};

export const setEditSessionTitle = (title) => {
  return async (dispatch, getState) => {
    const { editSession } = getState().session;
    const updatedSession = { ...editSession };

    updatedSession.title = title.substring(0, 200);

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: { ...updatedSession, modified: true },
    });
  };
};

export const setEditSessionType = (type) => {
  return async (dispatch, getState) => {
    const { editSession } = getState().session;
    const updatedSession = { ...editSession };

    updatedSession.type = type;

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: { ...updatedSession, modified: true },
    });
  };
};

export const setEditSessionDate = (dateStr) => {
  return async (dispatch, getState) => {
    const { editSession } = getState().session;
    const updatedSession = { ...editSession };

    updatedSession.dateStr = dateStr;

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: { ...updatedSession, modified: true },
    });
  };
};

export const setEditSessionStartTime = (startTimeStr) => {
  return async (dispatch, getState) => {
    const { editSession } = getState().session;
    const updatedSession = { ...editSession };

    updatedSession.startTimeStr = startTimeStr;
    updatedSession.estimatedDurationSeconds = calculateDurationSeconds(
      startTimeStr,
      editSession.endTimeStr
    );

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: { ...updatedSession, modified: true },
    });
  };
};

export const setEditSessionEndTime = (endTimeStr) => {
  return async (dispatch, getState) => {
    const { editSession } = getState().session;
    const updatedSession = { ...editSession };

    updatedSession.endTimeStr = endTimeStr;
    updatedSession.estimatedDurationSeconds = calculateDurationSeconds(
      editSession.startTimeStr,
      endTimeStr
    );

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: { ...updatedSession, modified: true },
    });
  };
};

export const setEditSession = (session) => {
  return async (dispatch) => {
    let editSession;
    if (session != null) {
      // create actual copy
      editSession = JSON.parse(JSON.stringify(session));
    } else {
      editSession = createEmptySession();
    }

    dispatch({
      type: Session.UPDATE_TARGET_SESSION,
      payload: editSession,
    });
  };
};

export const createEmptySession = () => {
  return { title: '', modified: false, type: SessionType.TEXT, startTimeStr: '', endTimeStr: '' };
};

const INIT_STATE = {
  editSession: createEmptySession(),
  activeSessionData: null,
  sessions: [],
  overallSessionCount: 0,
  fetchingSessions: false,
  sessionFetchError: null,
  savingSession: false,
  sessionSaveError: null,
  deletingSession: false,
  sessionDeleteError: null,
  initializingSession: false,
  sessionInitError: null,
  finishingSession: false,
  sessionFinishError: null,
};

export const sessionReducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case Session.INIT_SESSION_STARTED:
      return { ...state, initializingSession: true, sessionInitError: null };
    case Session.INIT_SESSION_FINISHED:
      return { ...state, initializingSession: false, sessionInitError: null };
    case Session.INIT_SESSION_ERROR:
      return { ...state, initializingSession: false, sessionInitError: action.payload };
    case Session.FETCH_SESSIONS_STARTED:
      return { ...state, fetchingSessions: true, sessionFetchError: null };
    case Session.FETCH_SESSIONS_FINISHED: {
      const { sessions, overallSessionCount } = action.payload;
      return { ...state, fetchingSessions: false, sessions, overallSessionCount };
    }
    case Session.FETCH_SESSIONS_ERROR: {
      return { ...state, fetchingSessions: false, sessionFetchError: action.payload };
    }
    case Session.CLEAR_SESSIONS: {
      return { ...state, sessions: [], overallSessionCount: 0 };
    }
    case Session.UPDATE_SESSION_LIST: {
      return { ...state, sessions: action.payload };
    }
    case Session.SAVE_SESSION_STARTED:
      return { ...state, savingSession: true, sessionSaveError: null };
    case Session.SAVE_SESSION_FINISHED:
      return { ...state, savingSession: false };
    case Session.SAVE_SESSION_ERROR:
      return { ...state, savingSession: false, sessionSaveError: action.payload };
    case Session.DELETE_SESSION_STARTED:
      return { ...state, deletingSession: true, sessionDeleteError: null };
    case Session.DELETE_SESSION_FINISHED:
      return { ...state, deletingSession: false };
    case Session.DELETE_SESSION_ERROR:
      return { ...state, deletingSession: false, sessionDeleteError: action.payload };
    case Session.UPDATE_TARGET_SESSION:
      return { ...state, editSession: action.payload };
    case Session.UPDATE_ACTIVE_SESSION_DATA: {
      return { ...state, activeSessionData: action.payload };
    }
    case Session.FINISH_SESSION_STARTED:
      return { ...state, finishingSession: true, sessionFinishError: null };
    case Session.FINISH_SESSION_FINISHED:
      return { ...state, finishingSession: false };
    case Session.FINISH_SESSION_ERROR:
      return { ...state, finishingSession: false, sessionFinishError: action.payload };
    default:
      return state;
  }
};
